import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AttendanceService } from '../../../../../services/attendance';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { FlashService } from '../../../../../services/flash';
import { IAttendanceReports } from '../../../../../interfaces/attendance';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Helpers } from '../../../../../utilities/helpers';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { Pagination } from '../../../../../shared/pagination/pagination';

interface IAttendanceSummary {
  total: number;
  present: number;
  absent: number;
  presentPercentage: number;
  absentPercentage: number;
}
@Component({
  selector: 'app-attendance-history',
  imports: [NgClass, FormsModule, LoadingIndicator, Pagination],
  templateUrl: './attendance-history.html',
  styleUrl: './attendance-history.css',
})
export class AttendanceHistory {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private attendanceService: AttendanceService,
  ) {
    this.route.queryParamMap.subscribe((params) => {
      this.classId = params.get('id')!;
    });

    if (!this.classId) {
      void this.router.navigate(['/dashboard/class']);
    }

    if (this.attendanceDate) this.getAttendanceHistory(true);
  }

  protected classId!: string;
  protected attendanceReports = signal<IAttendanceReports | null>(null);
  protected search = '';
  protected attendanceStatus = '';
  protected attendanceDate!: string;
  protected isFiltering = signal<boolean>(false);
  protected pagination = signal({ page: 0, size: 10 });
  protected readonly flashService = inject(FlashService);
  protected readonly Helpers = Helpers;
  protected attendanceSummary: IAttendanceSummary | null = null;

  protected onDateChange(): void {
    this.pagination.update((p) => ({ ...p, page: 0 }));

    if (!this.attendanceDate) {
      this.attendanceReports.set(null);
      this.attendanceSummary = null;
      this.isFiltering.set(false);
      return;
    }

    this.getAttendanceHistory(true);
  }

  protected applyFilters(): void {
    if (!this.attendanceDate) {
      return;
    }

    this.pagination.update((p) => ({ ...p, page: 0 }));

    this.getAttendanceHistory(false);
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({ ...p, page }));

    this.getAttendanceHistory(false);
  }

  protected navigateToStudentDetail(id: string): void {
    void this.router.navigate(['/dashboard/academic-management/student-details'], {
      queryParams: {
        id: id,
      },
    });
  }

  protected getAttendanceHistory(updateSummary = false): void {
    this.isFiltering.set(true);

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
      class_id: this.classId,
      attendance_date: this.attendanceDate,
    };

    const search = this.search.trim();

    if (search) {
      params['search'] = search;
    }

    if (this.attendanceStatus) {
      params['attendance_status'] = this.attendanceStatus;
    }

    this.attendanceService
      .getAttendanceReports(`${ApiEndpoints.url}/attendance-record`, params)
      .subscribe({
        next: (data) => {
          if (updateSummary) {
            this.attendanceSummary = this.getAttendanceSummary(data);
          }

          this.attendanceReports.set(data);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.isFiltering.set(false);
          this.flashService.failure(err?.error?.message ?? 'Could not load students. Refresh the page');
        },
      });
  }

  protected getAttendanceSummary(report: IAttendanceReports): IAttendanceSummary | null {
    if (!report) {
      return null;
    }

    const records = report?.payload ?? [];
    const total = records.length;

    const present = records.filter((r) => r.attendance_status?.toUpperCase() === 'PRESENT').length;

    const absent = records.filter((r) => r.attendance_status?.toUpperCase() === 'ABSENT').length;

    const presentPercentage = total > 0 ? (present / total) * 100 : 0;
    const absentPercentage = total > 0 ? (absent / total) * 100 : 0;

    return {
      total,
      present,
      absent,
      presentPercentage: Number(presentPercentage.toFixed(2)),
      absentPercentage: Number(absentPercentage.toFixed(2)),
    };
  }
}